import {
  lastOrUndefined,
  PipelineEntity,
  pipelineGraphsSelector,
  useGetPipelineGraphsByPipelineQuery
} from "@ots-frontend/ots-data";
import {ActionIcon, Card, Container, Divider, Grid, Group, Skeleton, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {ObjectMappingDataTableCard} from "../../card/ObjectMappingDataTableCard/ObjectMappingDataTableCard";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {Badge} from "../../badge/Badge/Badge";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {useCallback, useMemo} from "react";
import {Empty} from "../../empty/Empty/Empty";
import {PipelineGraphTimelineView} from "../../graph/PipelineGraphTimelineView/PipelineGraphTimelineView";
import {IconPlus} from "@tabler/icons-react";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {PipelineGraphDataTable} from "../../data-table/PipelineGraphDataTable/PipelineGraphDataTable";
import {useNavigate} from "react-router-dom";
import {DefaultText} from "@ots-frontend/ots-ui";

export type PipelineDetailsProps = {
  pipeline: PipelineEntity
}

export const PipelineDetails = (props: PipelineDetailsProps) => {
  const {pipeline} = props;

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const selectPipelineGraphs = useMemo(() => {
    return pipelineGraphsSelector();
  }, []);

  const {pipelineGraphs, isPipelineGraphsLoading} = useGetPipelineGraphsByPipelineQuery(pipeline.id, {
    selectFromResult: (result) => ({
      pipelineGraphs: selectPipelineGraphs(result, {pipelineId: pipeline.id}),
      isPipelineGraphsLoading: result.isLoading
    })
  });

  const onNewPipelineGraphClick = useCallback(() => {
    const sortedGraphs = pipelineGraphs?.sort((a, b) => a.effectiveDttm.valueOf() - b.effectiveDttm.valueOf());

    navigate("graph-builder", {
      state: {
        pipelineId: pipeline.id,
        pipelineGraphId: lastOrUndefined(sortedGraphs)?.id,
        newVersion: true,
      }
    });
  }, [navigate, pipeline, pipelineGraphs])

  return (
    <Grid>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.pipeline.info.section.details.label", "Details")}</Title>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={12}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.pipeline.info.field.name.label", "Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {pipeline.name}
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.pipeline.info.field.company.label", "Company")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={pipeline.companyName} />
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.pipeline.info.field.category.label", "Category")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDatabaseCode value={pipeline.category}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.pipeline.info.field.freighttypecategory.label", "Sub Category")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {pipeline.freightTypeCategory}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.pipeline.info.field.allocation.label", "Allocation")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDatabaseCode value={pipeline.allocationType}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.pipeline.info.field.temporary.label", "Temporary")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {pipeline.temporary ? (
                  <Badge color="orange">
                    {t("common.shared.temporary", "Temporary")}
                  </Badge>
                ) : t("common.shared.no", "No")
                }
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.pipeline.info.field.inactive.label", "Inactive")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {pipeline.inactiveDttm ? (
                  <Badge color="red">
                    <FormattedDateTime date={pipeline.inactiveDttm}/>
                  </Badge>
                ) : t("common.shared.no", "No")
                }
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.pipeline.info.field.usable.label", "Usable")}
              </Text>
              <Text ta="left" c={pipeline.doNotUse ? "warning" : "dimmed"} fz="xs">
                {pipeline.doNotUse ? t("common.shared.no", "No") : t("common.shared.yes", "Yes")}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title
              order={4}>{t("profile.pipeline.info.section.graphtimeline.label", "Graph Timeline")}</Title>
          </Group>

          <Divider/>

          <Grid>
            {isPipelineGraphsLoading ? (
              <Grid.Col span={12} m="sm">
                <Skeleton height={50} circle mb="xl"/>
                <Skeleton height={8} radius="xl"/>
                <Skeleton height={8} mt={6} radius="xl"/>
                <Skeleton height={8} mt={6} width="70%" radius="xl"/>
              </Grid.Col>
            ) : (
              pipelineGraphs.length > 0 ? (
                  <Container w={"100%"} h="400px" m="sm" fluid={true}>
                    <PipelineGraphTimelineView h="100%" pipelineGraphs={pipelineGraphs}/>
                  </Container>
                )
                : (
                  <Empty
                    maw={400}
                    h={100}
                    mx="auto"
                    m="sm"
                    message={"No Graphs"}/>
                )
            )
            }
          </Grid>
        </Card>
      </Grid.Col>
      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.pipeline.info.section.graphs.label", "Graphs")}</Title>
            <Group spacing={4} position="left" noWrap>
              <HasAccess permissions={["edit"]}>
                <ActionIcon color="green" onClick={() => onNewPipelineGraphClick()}>
                  <IconPlus size={16}/>
                </ActionIcon>
              </HasAccess>
            </Group>
          </Group>

          <Divider pb="sm"/>

          <PipelineGraphDataTable
            withBorder
            records={pipelineGraphs}
            fetching={isPipelineGraphsLoading}/>
        </Card>
      </Grid.Col>
      <HasAccess permissions={["object_mapping_view"]}>
        <Grid.Col span={12}>
          <ObjectMappingDataTableCard entity={pipeline}/>
        </Grid.Col>
      </HasAccess>
    </Grid>
  );
};

